import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import { useIsMounted } from './useIsMounted';
import { store } from '../store';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type ResponseData<ResponseType> =
  | InitialResponse
  | PendingResponse
  | SuccessResponse<ResponseType>
  | ErrorResponse;

type SuccessResponse<ResponseType = unknown> = {
  initial: false;
  pending: false;
  success: true;
  error: false;
  code: number;
  data: ResponseType;
  meta?: any;
};

export type ErrorResponse = {
  initial: false;
  pending: false;
  success: false;
  error: true;
  code: number;
  reason: string;
  meta?: any;
};

type InitialResponse = { initial: true; pending: false; success: false; error: false };
const defaultInitialResponse: InitialResponse = {
  initial: true,
  pending: false,
  success: false,
  error: false,
};

type PendingResponse = { initial: false; pending: true; success: false; error: false; meta?: any };

export type UseFetchConfig<T = any> = Partial<{
  method: Method;
  mapper?: (response: any) => T;
  includeCredentials?: boolean;
}>;
export const defaultApiMapper = (response: any) => response.data;
export const defaultConfig: UseFetchConfig = { method: 'GET', mapper: defaultApiMapper };

export type RequestConfig<RequestBodyType> = {
  headers?: Record<string, string>;
  body?: RequestBodyType | FormData;
  path?: string;
  params?: { [key: string]: string };
  meta?: any;
  multiPartFormData?: boolean;
};

const useFetch = <ResponseType, RequestBodyType = unknown>(
  path?: string,
  config?: UseFetchConfig<ResponseType>
): [
  ResponseData<ResponseType>,
  (config?: RequestConfig<RequestBodyType>) => void,
  (path?: string) => void | undefined
] => {
  const { t } = useTranslation();
  const { method, mapper, includeCredentials } = { ...defaultConfig, ...config };
  const { jwt } = store.getState().auth;
  const isMounted = useIsMounted();

  const [responseData, setResponseData] =
    useState<ResponseData<ResponseType>>(defaultInitialResponse);

  const updateResponse = useCallback(
    (responseData: ResponseData<ResponseType>) => {
      if (isMounted.current) setResponseData(responseData);
    },
    [isMounted]
  );

  const reInitialize = useCallback(() => {
    updateResponse(defaultInitialResponse);
  }, [updateResponse]);

  const doFetch = useCallback(
    (config: RequestConfig<RequestBodyType> = {}) => {
      const { body, params = {}, path: newPath, meta } = config;

      const finalPath = newPath || path;
      if (!finalPath) throw Error('Must give path when fetching!');
      const pathWithParams = Object.keys(params).reduce((acc, key) => {
        const regExp = new RegExp(`:${key}`, 'g');
        return acc.replace(regExp, params[key]);
      }, finalPath);

      const asyncFunction = async () => {
        updateResponse({
          initial: false,
          pending: true,
          success: false,
          error: false,
          meta,
        });

        const response = await fetch(pathWithParams, {
          method,
          body: config.multiPartFormData ? (body as FormData) : JSON.stringify(body),
          headers: {
            Authorization: `Bearer ${jwt}`,
            ...(!config.multiPartFormData && { 'Content-Type': 'application/json' }),
            ...(!config.multiPartFormData && { Accept: 'application/json' }),
            ...config.headers,
          },
          credentials: includeCredentials ? 'include' : 'same-origin',
        }).catch((reason) => console.log(reason, 'backend not reachable'));

        if (response) {
          const code = response ? response.status : 404;

          if (code === 204) {
            updateResponse({
              initial: false,
              pending: false,
              success: true,
              error: false,
              data: {} as ResponseType,
              code,
              meta,
            });
            return;
          }

          let responseJson;
          let reason;
          try {
            responseJson = await response.json();

            if (code === 200) {
              const data = typeof mapper === 'function' ? mapper(responseJson) : responseJson;
              updateResponse({
                initial: false,
                pending: false,
                success: true,
                error: false,
                code,
                data: data,
                meta,
              });
            } else {
              reason =
                get(responseJson, 'data.reason.message', '') ||
                get(responseJson, 'data.reason', '');

              reason = reason && typeof reason === 'string' ? reason : t('shared.errors.generic');

              updateResponse({
                initial: false,
                pending: false,
                success: false,
                error: true,
                code,
                reason,
                meta,
              });
              return;
            }
          } catch (e) {
            console.error(e);
            reason = t('shared.errors.generic');
            updateResponse({
              initial: false,
              pending: false,
              success: false,
              error: true,
              code,
              reason,
              meta,
            });
            return;
          }
        } else {
          updateResponse({
            initial: false,
            pending: false,
            success: false,
            error: true,
            code: 404,
            reason: 'backend not reachable',
            meta,
          });
        }
      };

      asyncFunction().then();
    },
    [jwt, mapper, method, path, t, updateResponse, includeCredentials]
  );

  return [responseData, doFetch, reInitialize];
};

export default useFetch;
